import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { getSocket } from '../utils/socketManager';
import { apiClient } from '../api/client';
import { getSocketBaseUrl } from '../utils/socketUtils.js';

const REQUEST_TIMEOUT = 30000; // 30 seconds

// Module-level cache to persist across component unmounts/remounts
const globalDataCache = new Map();

/**
 * Hook to fetch map analysis data for a specific view and pillar
 * @param {string} viewId - The view ID
 * @param {string} pillarId - The pillar ID
 * @returns {Object} The map analysis data and loading state
 */
export function useMapAnalysis(viewId, pillarId) {
  const [mapAnalysisData, setMapAnalysisData] = useState(null);
  const [isLoading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [currentMapAnalysisId, setCurrentMapAnalysisId] = useState(null);
  const pinnedMapAnalysisIdRef = useRef(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [generationError, setGenerationError] = useState(null);
  
  // Request tracking to prevent race conditions
  const currentRequestId = useRef(null);
  const abortControllerRef = useRef(null);
  const socketRef = useRef(null);
  const lastSocketUpdateRef = useRef(0);
  const lastSuccessfulFetchAtRef = useRef(0);
  
  // Memoize the cache key
  const cacheKey = useMemo(() => {
    return `${viewId}_${pillarId}`;
  }, [viewId, pillarId]);

  // Fetch data with retry mechanism
  const fetchData = useCallback(async (forceRefetch = false) => {
    if (!viewId || !pillarId) {
      setMapAnalysisData(null);
      setLoading(false);
      setError(null);
      setCurrentMapAnalysisId(null);
      pinnedMapAnalysisIdRef.current = null;
      return;
    }

    if (!forceRefetch && pinnedMapAnalysisIdRef.current) {
      return;
    }

    // Cancel any existing request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // Create new request ID and abort controller
    const requestId = `${viewId}_${pillarId}_${Date.now()}`;
    currentRequestId.current = requestId;
    const controller = new AbortController();
    abortControllerRef.current = controller;
    let timeoutId = null;

    try {
      setLoading(true);
      
      timeoutId = setTimeout(() => {
        controller.abort();
      }, REQUEST_TIMEOUT);

      if (!forceRefetch) {
        const cachedData = globalDataCache.get(cacheKey);
        if (cachedData) {
          setMapAnalysisData(cachedData);
          setCurrentMapAnalysisId(cachedData.id || null);
          setLoading(false);
          clearTimeout(timeoutId);
          return;
        }
      }

      const endpointUrl = `/map-analysis/view/${viewId}/pillar/${pillarId}`;

      const response = await apiClient.get(endpointUrl, {
        signal: controller.signal
      });

      clearTimeout(timeoutId);
      
      if (currentRequestId.current !== requestId) {
        return;
      }

      if (response.data && response.data.success && Array.isArray(response.data.data) && response.data.data.length > 0) {
        const mapAnalysisObj = response.data.data[0];
        globalDataCache.set(cacheKey, mapAnalysisObj);
        setMapAnalysisData(mapAnalysisObj);
        setCurrentMapAnalysisId(mapAnalysisObj.id || null);
        setError(null);
        lastSuccessfulFetchAtRef.current = Date.now();
      } else if (response.data && response.data.success && Array.isArray(response.data.data) && response.data.data.length === 0) {
        setMapAnalysisData(null);
        setCurrentMapAnalysisId(null);
        setError('No map analysis available for this selection.'); 
        lastSuccessfulFetchAtRef.current = Date.now();
      } else {
        setMapAnalysisData(null);
        setCurrentMapAnalysisId(null);
        setError('Failed to retrieve map analysis data');
      }
    } catch (err) {
      if (timeoutId) clearTimeout(timeoutId);
      
      if (currentRequestId.current !== requestId) {
        return;
      }

      const isCanceled = err?.name === 'CanceledError' || err?.code === 'ERR_CANCELED';
      if (isCanceled) {
        return;
      }
      
      let errMsg = 'An unknown error occurred';
      if (err.name === 'AbortError') {
        errMsg = 'Request timed out';
      } else {
        errMsg = err.message || 'Failed to fetch map analysis data'; 
      }
      
      setError(errMsg);
      setMapAnalysisData(null);
      setCurrentMapAnalysisId(null);
    } finally {
      if (timeoutId) clearTimeout(timeoutId);
      
      if (currentRequestId.current === requestId) {
        setLoading(false);
      }
      
      if (abortControllerRef.current === controller) {
        abortControllerRef.current = null;
      }
    }
  }, [viewId, pillarId, cacheKey]);

  const loadMapAnalysisById = useCallback(async (analysisId) => {
    if (!analysisId) return;

    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    const requestId = `map_analysis_by_id_${analysisId}_${Date.now()}`;
    currentRequestId.current = requestId;
    const controller = new AbortController();
    abortControllerRef.current = controller;
    let timeoutId = null;

    try {
      setLoading(true);
      setError(null);
      pinnedMapAnalysisIdRef.current = analysisId;

      timeoutId = setTimeout(() => {
        controller.abort();
      }, REQUEST_TIMEOUT);

      const response = await apiClient.get(`/map-analysis/${analysisId}`, {
        signal: controller.signal
      });

      clearTimeout(timeoutId);

      if (currentRequestId.current !== requestId) {
        return;
      }

      if (response.data && response.data.success) {
        setMapAnalysisData(response.data.data);
        setCurrentMapAnalysisId(response.data.data?.id || null);
        setError(null);
        lastSuccessfulFetchAtRef.current = Date.now();
      } else {
        setMapAnalysisData(null);
        setCurrentMapAnalysisId(null);
        setError(response.data?.message || 'Failed to load map analysis');
      }
    } catch (err) {
      if (timeoutId) clearTimeout(timeoutId);

      if (currentRequestId.current !== requestId) {
        return;
      }

      const isCanceled = err?.name === 'CanceledError' || err?.code === 'ERR_CANCELED';
      if (isCanceled) {
        return;
      }

      const errMsg = err?.name === 'AbortError' ? 'Request timed out' : (err?.message || 'Failed to load map analysis');
      setError(errMsg);
    } finally {
      if (timeoutId) clearTimeout(timeoutId);

      if (currentRequestId.current === requestId) {
        setLoading(false);
      }

      if (abortControllerRef.current === controller) {
        abortControllerRef.current = null;
      }
    }
  }, []);

  // Initial data fetch when viewId or pillarId changes
  useEffect(() => {
    if (viewId && pillarId) {
      fetchData();
    } else {
      setMapAnalysisData(null);
      setLoading(false); 
      setError(null);
      setCurrentMapAnalysisId(null); 
    }
  }, [viewId, pillarId, fetchData]);

  useEffect(() => {
    pinnedMapAnalysisIdRef.current = null;
  }, [viewId, pillarId]);

  // Clear cache and cancel requests when component unmounts
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        abortControllerRef.current = null;
      }
      // Note: We do NOT clear globalDataCache here, as we want it to persist across remounts
    };
  }, []);

  // Live updates via WebSocket
  useEffect(() => {
    // We need to keep track of the specific handlers to remove them later
    let activeSocket = null;
    let handleConnect = null;
    let handleMapAnalysisUpdate = null;
    let handleDataUpdate = null;

    if (!viewId || !pillarId) {
      return;
    }

    const baseUrl = getSocketBaseUrl();
    if (!baseUrl) {
      return;
    }

    const timeoutId = setTimeout(() => {
      // Use shared socket manager
      const socket = getSocket('/map-analysis-updates');
      if (!socket) return;

      activeSocket = socket;
      socketRef.current = socket;

      // Ensure socket is connected
      if (socket.disconnected) {
        socket.connect();
      }

      handleMapAnalysisUpdate = (payload) => {
        if (!payload) return;
        const payloadView = payload.view_id ?? payload.viewId;
        const payloadPillar = payload.pillar_id ?? payload.pillarId;
        
        // CRITICAL: Reject updates without valid view_id AND pillar_id
        // Empty broadcasts were clearing valid data
        if (payloadView === undefined || payloadPillar === undefined) {
          console.log('[useMapAnalysis] Skipping - missing view_id or pillar_id:', { payloadView, payloadPillar });
          return;
        }
        
        if (Number(payloadView) !== Number(viewId)) {
          return;
        }
        if (Number(payloadPillar) !== Number(pillarId)) {
          return;
        }
        
        console.log('[useMapAnalysis] Updating map analysis data from WebSocket');
        setMapAnalysisData(payload);
        setCurrentMapAnalysisId(payload.id || null);
        setLoading(false);
        setError(null);
        lastSuccessfulFetchAtRef.current = Date.now();
        
        // Also update cache
        const key = `${viewId}_${pillarId}`;
        globalDataCache.set(key, payload);
      };

      handleDataUpdate = (payload) => {
        if (!payload || payload.type !== 'map_analysis') return;
        const rows = Array.isArray(payload.data) ? payload.data : [payload.data];
        const matchingRow = rows.find((row) => {
          if (!row) return false;
          const rowView = row.view_id ?? row.viewId;
          const rowPillar = row.pillar_id ?? row.pillarId;
          // Must have both view_id and pillar_id to be valid
          if (rowView === undefined || rowPillar === undefined) return false;
          return (
            Number(rowView) === Number(viewId) &&
            Number(rowPillar) === Number(pillarId)
          );
        });
        if (matchingRow) {
          console.log('[useMapAnalysis] Found matching row in data_update, updating');
          setMapAnalysisData(matchingRow);
          setCurrentMapAnalysisId(matchingRow.id || null);
          setLoading(false);
          setError(null);
          lastSuccessfulFetchAtRef.current = Date.now();
          
          // Also update cache
          const key = `${viewId}_${pillarId}`;
          globalDataCache.set(key, matchingRow);
        }
      };

      handleConnect = () => {
        socket.emit('request_data', {
          view_id: Number(viewId),
          pillar_id: Number(pillarId)
        });
        lastSocketUpdateRef.current = Date.now();
      };

      if (socket.connected) {
        handleConnect();
      }

      socket.on('connect', handleConnect);
      socket.on('map_analysis_update', handleMapAnalysisUpdate);
      socket.on('data_update', handleDataUpdate);
    }, 500);

    return () => {
      clearTimeout(timeoutId);
      if (activeSocket) {
        if (handleConnect) activeSocket.off('connect', handleConnect);
        if (handleMapAnalysisUpdate) activeSocket.off('map_analysis_update', handleMapAnalysisUpdate);
        if (handleDataUpdate) activeSocket.off('data_update', handleDataUpdate);
      }
      socketRef.current = null;
    };
  }, [viewId, pillarId, fetchData]);

  const generateAnalysis = useCallback(async (generationViewId, generationPillarId, theme = null, model = null) => {
    if (!generationViewId || !generationPillarId) {
      setGenerationError('View and Pillar ID are required for generation.');
      setIsGenerating(false);
      return;
    }
    setIsGenerating(true);
    setGenerationError(null);
    try {
      const payload = {
        view_id: generationViewId,
        pillar_id: generationPillarId,
        theme: theme,
        model: model
      };

      const response = await apiClient.post('/map-analysis/generate', payload, { timeout: 120000 });
      
      fetchData(true); 
      setIsGenerating(false);
      return response.data;
    } catch (error) {
      setGenerationError(error.response?.data?.error || error.message || 'Failed to generate map analysis');
      setIsGenerating(false);
      throw error;
    }
  }, [fetchData]);

  return {
    mapAnalysisData,
    isLoading,
    error,
    lastUpdate,
    currentMapAnalysisId,
    refetch: () => fetchData(true),
    loadMapAnalysisById,
    generateAnalysis,
    isGenerating,
    generationError
  };
}

export default useMapAnalysis;
